/**
 * OfflineStatusIndicator - Shows offline status and queued items
 */

import { useState, useEffect } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Wifi, WifiOff, CloudOff, RefreshCw, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import {
  getOfflineQueue,
  syncOfflineQueue,
  getCachedCommands,
} from "../lib/offlineVoiceCommands";

export default function OfflineStatusIndicator({ compact = false }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueLength, setQueueLength] = useState(0);
  const [cachedCommands, setCachedCommands] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check queue and cache
    const checkStatus = async () => {
      const queue = await getOfflineQueue();
      setQueueLength(queue.length);
      const commands = await getCachedCommands();
      setCachedCommands(commands.length);
    };
    checkStatus();
    const interval = setInterval(checkStatus, 10000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  const handleSync = async () => {
    if (!isOnline) {
      toast.error("Cannot sync while offline");
      return;
    }
    setSyncing(true);
    try {
      const result = await syncOfflineQueue(process.env.REACT_APP_BACKEND_URL);
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} items`);
      } else {
        toast.info("Nothing to sync");
      }
      const queue = await getOfflineQueue();
      setQueueLength(queue.length);
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
              isOnline ? "text-green-500" : "text-yellow-500 bg-yellow-500/10"
            }`}>
              {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {queueLength > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-xs">
                  {queueLength}
                </Badge>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isOnline ? "Online" : "Offline mode"}</p>
            <p className="text-xs text-muted-foreground">
              {cachedCommands} commands cached
              {queueLength > 0 && `, ${queueLength} items queued`}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg ${
      isOnline 
        ? "bg-green-500/10 border border-green-500/20" 
        : "bg-yellow-500/10 border border-yellow-500/20"
    }`}>
      {isOnline ? (
        <Wifi className="w-4 h-4 text-green-500" />
      ) : (
        <CloudOff className="w-4 h-4 text-yellow-500" />
      )}
      
      <div className="flex-1">
        <p className={`text-sm font-medium ${isOnline ? "text-green-500" : "text-yellow-500"}`}>
          {isOnline ? "Online" : "Offline Mode"}
        </p>
        <p className="text-xs text-muted-foreground">
          {cachedCommands} voice commands cached
          {queueLength > 0 && (
            <span className="text-yellow-500 ml-1">• {queueLength} pending sync</span>
          )}
        </p>
      </div>

      {queueLength > 0 && isOnline && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleSync}
          disabled={syncing}
          className="h-7"
        >
          {syncing ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
        </Button>
      )}
    </div>
  );
}
